// Lomi Live — the permanent, shareable keepsake. Every competitor leaves with a
// personalized card (name · school · date · score) they can save and post. It's
// their badge of honor AND our next-year recruiting fuel. Mirrors the 10k
// "souvenir" feel: dark citrus stage, Ethiopian-flag ribbon, date-stamped.

import React, { useEffect, useRef, useState } from 'react';
import { liveCardSparkle } from '../../utils/celebrate';
import './LiveKeepsake.css';

interface Props {
  name: string;
  school?: string | null;
  // e.g. "Biology · 28/30" once the score is visible, else a stream tagline
  line?: string | null;
  champion?: boolean; // gold/champion variant
  dateLabel?: string; // e.g. "Sat, Jun 20, 2026"
}

// html2canvas, lazily from CDN — degrades to a screenshot hint if it can't load.
function loadH2C(): Promise<any> {
  const w = window as any;
  if (w.html2canvas) return Promise.resolve(w.html2canvas);
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
    s.async = true;
    s.onload = () => resolve(w.html2canvas || null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}

const LiveKeepsake: React.FC<Props> = ({ name, school, line, champion, dateLabel }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    void liveCardSparkle();
  }, []);

  const save = async () => {
    setBusy(true);
    setHint(null);
    try {
      const h2c = await loadH2C();
      if (!h2c || !cardRef.current) {
        setHint('Take a screenshot to save your card 📸');
        return;
      }
      const canvas = await h2c(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true });
      const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      if (!blob) {
        setHint('Take a screenshot to save your card 📸');
        return;
      }
      const file = new File([blob], 'lomi-live-card.png', { type: 'image/png' });
      const nav = navigator as any;
      // Prefer native share (mobile) so they can post straight to socials.
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Lomi Live', text: 'I competed in the Lomi Live Mock Exam 🍋🏆' });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'lomi-live-card.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      setHint('Take a screenshot to save your card 📸');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lk-wrap" data-testid="live-keepsake">
      <div className={`lk-card${champion ? ' lk-champ' : ''}`} ref={cardRef}>
        <div className="lk-ribbon" />
        <div className="lk-brand">
          <img src="/logo192.png" alt="Lomi" className="lk-logo" crossOrigin="anonymous" />
          <span className="lk-wm">Lomi <b>Live</b></span>
        </div>
        <div className="lk-eyebrow">{champion ? 'Champion' : 'I competed in'}</div>
        <div className="lk-event">Lomi Live Mock Exam</div>
        <div className="lk-name">{name || 'Lomi Scholar'}</div>
        {school ? <div className="lk-school">🏫 {school}</div> : null}
        {line ? <div className="lk-line">{line}</div> : null}
        <div className="lk-rule" />
        <div className="lk-foot">
          <span>{dateLabel || 'Lomi Live'}</span>
          <span className="lk-dot">·</span>
          <span>lomistudy.com</span>
        </div>
        <div className="lk-am">🍋 የሎሚ ኮከብ · Lomi Star</div>
      </div>
      <button className="lk-save" onClick={save} disabled={busy} data-testid="live-keepsake-save">
        {busy ? 'Preparing…' : '📸 Save & share your card'}
      </button>
      {hint && <div className="lk-hint">{hint}</div>}
    </div>
  );
};

export default LiveKeepsake;
